import { Box, Text } from '@kluein/klue-ui';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';

import TEST_IDS from 'test-ids';

import { ALL_GROUPS_GROUP } from '../dashboard-with-news/DashboardWithNews.constants';
import { RivalGroup } from '../rival-group';

import type { RivalGroupType } from 'api/api.types';
import type { useDashboardFilter } from 'hooks/dashboard';
import type { RootState } from 'store/store.types';

type DashboardRivalsProps = {
  rivalGroups: RivalGroupType[];
} & Pick<ReturnType<typeof useDashboardFilter>, 'group' | 'sort'>;

const ALL_COMPANIES_GROUP = 0;

export function DashboardRivals({
  rivalGroups: allRivalGroups,
  group,
  sort,
}: DashboardRivalsProps) {
  const { t } = useTranslation();

  const allRivals = useSelector<RootState, number[] | undefined>((state) => {
    const { isLoading, allIds } = state.rivals;
    if (!isLoading) {
      return [...allIds].map((id) => +id);
    }
  }, shallowEqual);

  let rivalGroups: {
    id?: number;
    name: string;
    isNotCollapsible?: boolean;
    rivals?: number[];
  }[] = allRivalGroups;
  if (group === ALL_COMPANIES_GROUP) {
    rivalGroups = [
      {
        id: undefined,
        isNotCollapsible: true,
        name: '',
        rivals: allRivals,
      },
    ];
  } else if (group) {
    const rivalGroup = allRivalGroups.find(({ id }) => id === group);
    if (rivalGroup) {
      rivalGroups = [
        {
          id: rivalGroup.id,
          isNotCollapsible: true,
          name: rivalGroup.name,
          rivals: rivalGroup.rivals,
        },
      ];
    }
  }

  return (
    <Box
      margin={{ horizontal: 'auto' }}
      fill="horizontal"
      align="center"
      pad={{ top: group === ALL_GROUPS_GROUP ? 'none' : 'large' }}
      data-test-id={TEST_IDS.dashboard.boardsSection}
    >
      {!!rivalGroups.length ? (
        rivalGroups.map(({ id, name, isNotCollapsible, rivals }, index) => {
          return (
            <RivalGroup
              id={id}
              sort={sort}
              name={name}
              rivals={rivals}
              key={id || 'all'}
              isCollapsible={!isNotCollapsible && id !== undefined}
              rowOrder={rivalGroups.length === 1 ? 0 : index + 1}
            />
          );
        })
      ) : (
        <Box fill align="center" justify="center">
          <Text size="large">{t('Dashboard:emptyRivalsGroup')}</Text>
        </Box>
      )}
    </Box>
  );
}
